import { NotificationAuthError, buildNotificationStreamUrl, getNotificationTicket } from '@/lib/notifications-client';
import type { NotificationEventType, NotificationPayload } from '@/types/notifications';

type Handler = (payload: NotificationPayload) => void;

const EVENT_TYPES: NotificationEventType[] = [
  'new_order',
  'conversation_switched_to_manual',
  'conversation_needs_intervention',
  // The orders board's live feed. Listened for here rather than on a transport of its own — SSE
  // listeners are per event name, so a board event with no listener would simply never arrive.
  'order.board.changed',
  // Standalone deliveries on the board ("Solo envío"): no order row, so no board event of their own.
  'delivery_changed',
  // A rider run that needs an operator — an alert, surfaced like the handoff ones.
  'delivery_alert',
  // A transfer-held cart whose placement was blocked once the receipt came in — a handoff-style alert.
  'transfer_order_needs_review',
];

class NotificationStream {
  private es: EventSource | null = null;
  private handlers = new Set<Handler>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempts = 0;
  private authFailures = 0;
  private closed = true;

  /**
   * How many consecutive authorization failures to tolerate before giving up. A delegated agent-ui
   * session has no refresh path, so once its token expires every retry returns the same 401 — left
   * unbounded that is one pointless request a minute for the life of the tab. A small budget still
   * absorbs a blip (the auth layer revalidates and may find the session fine) without the loop.
   */
  private static readonly MAX_AUTH_FAILURES = 3;

  async open() {
    if (!this.closed && this.es) return;
    this.closed = false;
    await this.connect();
  }

  /**
   * Stop the transport. Deliberately does NOT clear `handlers`: the set is shared by every
   * subscriber (the notification display, the orders board), and each one owns the unsubscribe this
   * class handed it. Clearing here would silently kill the OTHER subscribers whenever one of them
   * closes the stream — which `useNotificationStream` does on every session refresh, including the
   * routine one where the auth layer revalidates a transient 401 and finds the session healthy.
   * The board's subscription has no reason to re-run then, so it would stay dead for the tab.
   */
  close() {
    this.closed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.es?.close();
    this.es = null;
    this.attempts = 0;
    this.authFailures = 0;
  }

  subscribe(handler: Handler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  private async connect() {
    if (this.closed) return;
    try {
      const { ticket } = await getNotificationTicket();
      const es = new EventSource(buildNotificationStreamUrl(ticket));
      this.es = es;

      EVENT_TYPES.forEach((type) => {
        es.addEventListener(type, (e) => {
          try {
            const payload = JSON.parse((e as MessageEvent).data) as NotificationPayload;
            this.handlers.forEach((h) => h(payload));
          } catch (err) {
            console.warn('Failed to parse notification payload', err);
          }
        });
      });

      es.addEventListener('heartbeat', () => {});

      es.onopen = () => {
        this.attempts = 0;
        this.authFailures = 0;
      };

      es.onerror = () => this.scheduleReconnect();
    } catch (err) {
      if (err instanceof NotificationAuthError) {
        // The ticket client has already raised the auth event that drives re-authentication. Give
        // that a couple of chances to land, then stop: past this point the session is genuinely
        // unauthorized and every further attempt returns the same status.
        this.authFailures += 1;
        if (this.authFailures >= NotificationStream.MAX_AUTH_FAILURES) {
          console.warn('Notification stream not authorized — stopping until re-authenticated', err);
          this.closed = true;
          return;
        }
      }
      console.warn('Notification stream connect failed', err);
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    this.es?.close();
    this.es = null;
    if (this.closed) return;
    const delay = Math.min(3000 * Math.pow(2, this.attempts), 60_000);
    this.attempts += 1;
    this.reconnectTimer = setTimeout(() => void this.connect(), delay);
  }
}

export const notificationStream = new NotificationStream();
